import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@material-ui/core";
import { AssignmentOutlined as QuestionnaireIcon, ContentCopy, Download, OpenInNew } from "@material-ui/icons";
import type { SignatureDocument } from "../../../@types/signatures";
import { isQuestionnaireOnlyDocument } from "../../../@types/sharedSchema";
import { downloadPreparedPdfBlob, resendWhatsapp } from "../../../services/signatures";
import { deliveryRecipientLabel } from "../../../utils/signatureWhatsappShare";
import { carriesQuestionnaireStep, questionnaireListedForOf } from "../../../utils/signatureQuestionnaireStep";
import { questionnaireLabelContext } from "../../../utils/signatureQuestionnaireName";
import PreparedSignatureSetDialog from "./PreparedSignatureSetDialog";
import SigningLinkWhatsappShareButton from "./SigningLinkWhatsappShare";
import ForwardSendIcon from "../../ForwardSendIcon";

export interface PreparedSignatureResult {
  documents: SignatureDocument[];
  documentId?: string;
  bundleId?: string;
  failedDocumentCount?: number;
}

/** A set of contracts (a bundle preparation) is shared as one unit; one contract keeps its own dialog. */
export default function PreparedSignatureDialog({ result, onClose }: {
  result: PreparedSignatureResult;
  onClose: () => void;
}) {
  return result.documents.length > 1
    ? <PreparedSignatureSetDialog result={result} onClose={onClose} />
    : <PreparedSingleSignatureDialog result={result} onClose={onClose} />;
}

/** Actions operate on the archived contract and its real signing link. */
function PreparedSingleSignatureDialog({ result, onClose }: {
  result: PreparedSignatureResult;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState<"message" | "copy" | "download" | null>(null);
  const inFlight = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [messaged, setMessaged] = useState<string[]>([]);
  const document = result.documents[0];
  const signer = document?.signers[0];
  const signingUrl = signer?.signing?.publicSigningUrl;

  async function run(action: "message" | "copy" | "download") {
    if (!document || inFlight.current) return;
    inFlight.current = true;
    setBusy(action);
    setError(null);
    setNotice(null);
    try {
      if (action === "message" && signer && signingUrl) {
        const updated = await resendWhatsapp(document.id, signer.id);
        const delivery = updated.signers.find((item) => item.id === signer.id)?.whatsappDelivery;
        if (delivery?.status !== "sent") throw new Error("Delivery failed");
        setMessaged((ids) => [...ids, document.id]);
        setNotice(t("signatures.prepared.messageSent", { to: deliveryRecipientLabel(signer) }));
      } else if (action === "copy" && signingUrl) {
        await navigator.clipboard.writeText(signingUrl);
        setNotice(t("signatures.prepared.copied"));
      } else if (action === "download") {
        const blob = await downloadPreparedPdfBlob(document.id);
        const url = URL.createObjectURL(blob);
        const anchor = window.document.createElement("a");
        anchor.href = url;
        anchor.download = `${document.formattedNumber || document.id}.pdf`;
        window.document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        // Let the browser start consuming the download before releasing its URL.
        window.setTimeout(() => URL.revokeObjectURL(url), 60000);
      }
    } catch {
      setError(t(`signatures.prepared.${action}Error`));
    } finally {
      inFlight.current = false;
      setBusy(null);
    }
  }

  return <Dialog open onClose={() => { if (!inFlight.current) onClose(); }} fullWidth maxWidth="sm">
    {/* A questionnaire on its own is not a contract: "השאלון מוכן", under the name the client sees. */}
    <DialogTitle>{t(isQuestionnaireOnlyDocument(document) ? "signatures.prepared.titleQuestionnaire" : "signatures.prepared.title")}</DialogTitle>
    <DialogContent>
      <Stack spacing={2} sx={{ pt: 1 }}>
        <Typography color="text.secondary">{t("signatures.prepared.chooseAction")}</Typography>
        {!!result.failedDocumentCount && <Alert severity="warning">
          {t("signatures.prepared.partialFailure", { count: result.failedDocumentCount })}
        </Alert>}
        {document && <Box>
          <Typography fontWeight={600}>
            {(isQuestionnaireOnlyDocument(document) && document.templateCustomerFacingNameSnapshot) || document.templateNameSnapshot} · {document.formattedNumber}
          </Typography>
          {/* The contract carries the owner questionnaire: say so, under its name, before the realtor
              sends it (Trello #231, item 8 follow-up). A line, not a box: the five actions still fit a
              phone. A questionnaire on its own needs no such line — its title says what it is. */}
          {carriesQuestionnaireStep(document) && <Stack direction="row" alignItems="flex-start" spacing={1}
            data-testid="prepared-questionnaire-note" sx={{ mt: 0.75 }}>
            <QuestionnaireIcon fontSize="small" sx={{ color: "info.main", mt: "2px", flexShrink: 0 }} />
            <Typography variant="body2" sx={{ color: "info.dark", fontWeight: 600 }}>
              {t("signatures.prepared.questionnaireNote", { context: questionnaireLabelContext(questionnaireListedForOf(document)) })}
            </Typography>
          </Stack>}
        </Box>}
        {notice && <Alert severity="success">{notice}</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
        <Button fullWidth variant="contained" size="large"
          endIcon={busy === "message" ? <CircularProgress size={18} color="inherit" /> : <ForwardSendIcon />}
          disabled={!!busy || !signingUrl || messaged.includes(document?.id)} onClick={() => run("message")}>
          {t("signatures.prepared.sendMessage")}
        </Button>
        <SigningLinkWhatsappShareButton documents={result.documents} fullWidth size="large" showHint disabled={!!busy} />
        <Button fullWidth variant="outlined" size="large" startIcon={<ContentCopy />}
          disabled={!!busy || !signingUrl} onClick={() => run("copy")}>
          {t("signatures.prepared.copyLink")}
        </Button>
        <Button fullWidth variant="outlined" size="large" startIcon={<OpenInNew />}
          component="a" href={signingUrl} target="_blank" rel="noopener noreferrer" disabled={!!busy || !signingUrl}>
          {t("signatures.prepared.signInPerson")}
        </Button>
        <Button fullWidth variant="outlined" size="large"
          startIcon={busy === "download" ? <CircularProgress size={18} /> : <Download />}
          disabled={!!busy || !document?.sentPdfUrl} onClick={() => run("download")}>
          {t("signatures.prepared.downloadPdf")}
        </Button>
      </Stack>
    </DialogContent>
    <DialogActions><Button disabled={!!busy} onClick={onClose}>{t("signatures.prepared.close")}</Button></DialogActions>
  </Dialog>;
}
